import {
  TOTALS_COLUMN_LABEL,
  buildColumnPathKey,
  extractColumnValueIds,
  getColumnGroupName,
  getColumnPathLabel,
  isMultiHierarchyCalculatedColumnGroup,
  type ColumnValue,
  type IColumnSource
} from '@opengov/reporting-types';
import { countBy } from 'lodash';
import { useMemo } from 'react';
import type { SelectableItem } from '../FormulaInput.utils';

type UseBuildColumnOptionsArgs = {
  pivotColumns: ColumnValue[];
  columnGroups: IColumnSource[];
  excludeColumnGroupIds: string[];
};

/** Sentinel value id for a column group's totals column, which has no id of its own. */
const TOTALS_REFERENCE_ID = 'totals';

/**
 * Builds selectable column options from pivot column values.
 *
 * One option per LEAF column: a column value carries an id/label pair per organize-by level, so a
 * multi-level column group has several columns sharing a level-1 id. Keying on that id alone offered
 * the parent's name several times over and never offered the columns themselves.
 *
 * The groupId is the leaf's full level path, percent-encoded to match the formula's reference
 * pattern — the same encoding `buildColumnReferenceKey` uses service-side, so a picked option and
 * the column the formula resolves to cannot disagree. A one-segment path encodes to itself, so a
 * one-level column group produces exactly the options and references it always did.
 *
 * Totals columns use 'totals' as the groupId; other calculated columns use calcColId:calcColId.
 */
export function buildColumnOptions({
  pivotColumns,
  columnGroups,
  excludeColumnGroupIds
}: UseBuildColumnOptionsArgs): SelectableItem[] {
  const excludeSet = new Set(excludeColumnGroupIds);
  // Calculated columns are added separately with the calcColId:calcColId format — skip them from pivot columns.
  const calcColumnIds = new Set(columnGroups.filter(isMultiHierarchyCalculatedColumnGroup).map((cg) => cg.id));

  const columnGroupById = new Map(columnGroups.map((g) => [g.id, g]));
  const pivotOptions: SelectableItem[] = [];

  // Add data column values from pivot columns
  for (const columnValue of pivotColumns) {
    const columnGroupId = String(columnValue[0]);
    if (excludeSet.has(columnGroupId) || calcColumnIds.has(columnGroupId)) {
      continue;
    }
    const group = columnGroupById.get(columnGroupId);
    pivotOptions.push({
      parentGroupId: columnGroupId,
      groupId: buildOptionGroupId(columnValue),
      label: resolveColumnLabel({ group, columnValue })
    });
  }

  const options = disambiguateLabels({ options: pivotOptions, columnGroupById });

  // Add other calculated columns (using calcColId:calcColId format for calc-to-calc references)
  for (const colGroup of columnGroups) {
    if (isMultiHierarchyCalculatedColumnGroup(colGroup) && !excludeSet.has(colGroup.id)) {
      options.push({ parentGroupId: colGroup.id, groupId: colGroup.id, label: colGroup.alias });
    }
  }

  return options;
}

export function useBuildColumnOptions({
  pivotColumns,
  columnGroups,
  excludeColumnGroupIds
}: UseBuildColumnOptionsArgs): SelectableItem[] {
  return useMemo(
    () => buildColumnOptions({ pivotColumns, columnGroups, excludeColumnGroupIds }),
    [pivotColumns, columnGroups, excludeColumnGroupIds]
  );
}

/**
 * The value id half of a column's formula reference: its level path, percent-encoded.
 *
 * Must stay in step with `buildColumnReferenceKey` in the service's calc-column pipeline, which
 * builds the other side of the same lookup.
 */
function buildOptionGroupId(columnValue: ColumnValue): string {
  const pathIds = extractColumnValueIds(columnValue);
  const isTotals = pathIds.length === 0 || pathIds.every((id) => id === null);
  if (isTotals) return TOTALS_REFERENCE_ID;
  return encodeURIComponent(buildColumnPathKey(pathIds));
}

/**
 * The column's display label: every level of its path, outermost first.
 *
 * Labelling by the deepest level alone made the picker unusable for a column group organized by
 * several levels — it repeats its leaf labels under each parent, so the list held several identical
 * `FY2024` entries and `disambiguateLabels` below could not fix them, because appending the column
 * group name is no help when the collisions are inside one group.
 *
 * A one-level column still yields its bare label, so single-level reports read as before. Totals
 * carries a null id with a `Totals` label, so it keeps its own name.
 */
function resolveColumnLabel({
  group,
  columnValue
}: {
  group: IColumnSource | undefined;
  columnValue: ColumnValue;
}): string {
  return getColumnPathLabel(group, columnValue) || TOTALS_COLUMN_LABEL;
}

function disambiguateLabels({
  options,
  columnGroupById
}: {
  options: SelectableItem[];
  columnGroupById: Map<string, IColumnSource>;
}): SelectableItem[] {
  const labelCounts = countBy(options, (opt) => opt.label);
  const ambiguousLabels = new Set(Object.keys(labelCounts).filter((label) => labelCounts[label] > 1));

  return options.map((opt) => {
    if (!ambiguousLabels.has(opt.label)) {
      return opt;
    }
    const group = columnGroupById.get(opt.parentGroupId);
    const groupName = group ? getColumnGroupName(group) : undefined;
    return groupName ? { ...opt, label: `${opt.label} (${groupName})` } : opt;
  });
}
